import styled, { css } from 'styled-components';
import { down } from 'styled-breakpoints';
import { motion } from 'framer-motion';

import { PerfectScrollbar } from '@components';

export const Container = styled(motion.dialog)`
  ${({ theme }) => css`
    padding: 2.5rem 2rem;
    margin: auto;
    width: 100%;
    max-width: 60rem;
    min-height: 40rem;
    max-height: 90vh;
    border-radius: 1rem;
    position: relative;
    background: ${theme.colors.background[100]};
    color: ${theme.colors.foreground[100]};
    overflow-y: auto;

    box-shadow: 0px 2.8px 2.2px rgba(0, 0, 0, 0.02),
      0px 6.7px 5.3px rgba(0, 0, 0, 0.028), 0px 12.5px 10px rgba(0, 0, 0, 0.035),
      0px 22.3px 17.9px rgba(0, 0, 0, 0.042),
      0px 41.8px 33.4px rgba(0, 0, 0, 0.05), 0px 100px 80px rgba(0, 0, 0, 0.07);

    > h2 {
      padding-bottom: 0.5rem;
      border-bottom: 2px solid ${theme.colors.background[80]};
    }

    ${down('md')} {
      padding: 2.5rem 1.25rem;
      max-height: 95vh;
    }
  `}
`;

export const SurveyWrapper = styled(PerfectScrollbar)`
  ${({ theme }) => css`
    padding-top: 1rem;
    height: 100%;
    max-height: calc(100% - 2.5rem);

    .sv_main {
      height: 100%;

      * {
        box-sizing: border-box;
        min-width: 0;
      }

      span {
        word-break: break-word;
        white-space: pre-wrap;
      }

      > form {
        height: 100%;

        .sv_container {
          height: 100%;

          > div {
            height: 100%;
          }

          .sv_q_rating {
            fieldset {
              margin: 0 !important;

              .sv_q_rating_item_text {
                padding: 0 2em !important;
              }

              .sv_q_rating_item:nth-child(2)
                .sv_q_rating_item_text:not(:hover) {
                border-left-color: #e7e7e7 !important;
              }
            }
          }

          .sv_body {
            padding: 0;
            border: 0;
            height: 100%;
            display: flex;
            flex-direction: column;

            .sv_nav {
              margin-top: auto;
            }

            .sv_p_root {
              > .sv_row {
                border: 0;
                min-width: auto;

                > div {
                  min-width: auto !important;
                }
              }

              .sv_q {
                padding: 0.5em 0 1.5em;
              }

              .sv_q_erbox {
                border-radius: 0.5rem;
              }

              .sv_q_title {
                font-weight: 500;
                font-size: 1.25rem;
                color: ${theme.colors.foreground[100]};
              }

              .sv_q_description {
                margin-bottom: 1rem;
              }

              .sv-q-col-1 {
                padding: 0;

                & + .sv-q-col-1 {
                  margin-top: 0.5rem;
                }

                .sv_q_radiogroup_label,
                .sv_q_checkbox_label {
                  padding: 0;
                  cursor: pointer;

                  span:not(:last-child) {
                    height: 0;
                    display: none;
                  }

                  input {
                    opacity: 0;
                    height: 0;
                    width: 0;
                    display: none;

                    & ~ span:last-child {
                      display: block;
                      background: ${theme.colors.background[80]};
                      border: 1px solid ${theme.colors.background[60]};
                      padding: 0.5rem 0.75rem;
                      border-radius: 0.5rem;
                      transition: 180ms ease-in-out;
                    }

                    &:checked ~ span:last-child {
                      background: ${theme.colors.success[100]};
                      font-weight: 500;
                    }
                  }

                  :hover {
                    > span:last-child {
                      background: ${theme.colors.success[100]};
                    }
                  }
                }
              }

              input[type='text'] {
                border-radius: 0.5rem;
                padding: 0.5rem 1rem !important;
                height: initial !important;

                :focus {
                  border: 1px solid ${theme.colors.brand.primary.default} !important;
                }
              }
            }

            .sv_progress_bar {
              margin-top: auto;
              background: ${theme.colors.brand.primary.default};
              transition: 180ms ease-in-out;
            }

            input[type='button'],
            .sv_main button {
              background: ${theme.colors.brand.primary.default};
              font-size: 1rem;
              padding: 0.5rem 1rem;
              border-radius: 0.5rem;
              transition: 180ms ease-in-out;

              :hover {
                filter: brightness(1.25);
              }

              :active {
                filter: brightness(0.75);
              }
            }
          }
        }
      }
    }
  `}
`;

export const FinishedSurveyWrapper = styled.div`
  ${() => css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100% - 3rem);
    padding: 2rem 1rem;

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.5rem;
      max-width: 500px;
      width: 100%;
      text-align: center;
    }
  `}
`;

export const SuccessIconWrapper = styled.div`
  ${() => css`
    animation: scaleIn 0.5s ease-out;

    @keyframes scaleIn {
      from {
        transform: scale(0);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    svg {
      filter: drop-shadow(0 4px 12px rgba(72, 187, 120, 0.3));
    }
  `}
`;

export const TitleText = styled.h3`
  ${({ theme }) => css`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin: 0;
    line-height: 1.4;
  `}
`;

export const SubtitleText = styled.p`
  ${({ theme }) => css`
    font-size: 1rem;
    color: ${theme.colors.foreground[80]};
    margin: 0;
    line-height: 1.6;
  `}
`;

export const PasswordSection = styled.div`
  ${() => css`
    width: 100%;
    text-align: left;
  `}
`;

export const PasswordLabel = styled.label`
  ${({ theme }) => css`
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.5rem;
  `}
`;

export const PasswordInput = styled.input`
  ${({ theme }) => css`
    width: 100%;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    border: 1px solid ${theme.colors.background[60]};
    border-radius: 0.5rem;
    outline: none;
    transition: all 0.2s ease;
    background: ${theme.colors.background[100]};
    color: ${theme.colors.foreground[100]};

    &::placeholder {
      color: ${theme.colors.foreground[40]};
    }

    &:focus {
      border-color: ${theme.colors.brand.primary.default};
    }

    &:hover:not(:focus) {
      border-color: ${theme.colors.background[40]};
    }
  `}
`;

export const PasswordHint = styled.small`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: ${theme.colors.foreground[60]};
    margin-top: 0.5rem;

    svg {
      flex-shrink: 0;
    }
  `}
`;

export const InfoBox = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    background: ${theme.colors.warning[40]}15;
    border: 1px solid ${theme.colors.warning[40]};
    border-radius: 0.5rem;
    margin-top: 1rem;

    svg {
      flex-shrink: 0;
      margin-top: 0.125rem;
    }

    p {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: ${theme.colors.foreground[80]};
    }
  `}
`;

export const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  button {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
  }
`;
